import { useState, useRef, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { Send, Bot, User, Sparkles, Download, RefreshCw, Search, Globe, Check, X, Paperclip, FileText, Calendar, ChevronDown, ChevronRight, Clock, Zap, ArrowLeft } from 'lucide-react'
import { useAutonomousAgent, AUTONOMOUS_QUESTIONS } from '../../contexts/AutonomousAgentContext'
import { useDropzone } from 'react-dropzone'
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const SYSTEM_PROMPT = `Eres el Agente Estratégico de Next Valley Consultants (NVC). Lideras el proceso completo de planeación estratégica siguiendo la metodología NVC v2.0. Eres profesional, directo y cálido. Hablas en español.

La metodología tiene dos niveles:

════ NIVEL CONSULTORÍA (aplica a toda la empresa) ════

FASE 1 — ANÁLISIS MACRO (PESTEL) — 7 secciones:
1.1 Análisis Político: Regulaciones vigentes y esperadas, estabilidad política, política comercial, leyes de competencia, incentivos gubernamentales al sector. (3 tareas: mapear regulaciones, evaluar estabilidad política, identificar leyes de competencia e incentivos)
1.2 Análisis Económico: Crecimiento del PIB, inflación, tasas de interés, tipo de cambio, poder adquisitivo del consumidor, ciclos económicos relevantes. (3 tareas: analizar indicadores macro, evaluar poder adquisitivo y ciclos, identificar riesgos económicos)
1.3 Análisis Social: Cambios demográficos, tendencias de consumo, valores culturales, estilos de vida, urbanización, penetración tecnológica. (3 tareas: mapear cambios demográficos, analizar tendencias consumo, identificar valores y estilos de vida)
1.4 Análisis Tecnológico: Innovaciones disruptivas, digitalización del canal, automatización, e-commerce, datos y analytics. (3 tareas: identificar innovaciones disruptivas, evaluar digitalización, analizar impacto e-commerce)
1.5 Análisis Ambiental/ESG: Regulaciones ambientales, tendencias de sostenibilidad, expectativas de consumidores sobre RSE y huella ambiental. (3 tareas: mapear regulaciones ambientales, analizar tendencias sostenibilidad, evaluar expectativas RSE)
1.6 Análisis Legal: Normativas de etiquetado, publicidad, protección al consumidor, propiedad intelectual, contratos laborales, ley sectorial. (3 tareas: revisar normativas etiquetado y publicidad, evaluar protección al consumidor, identificar riesgos legales)
1.7 Supuestos Estratégicos y Megatendencias: Consolidar 3-5 megatendencias clave del análisis PESTEL y definir supuestos estratégicos que condicionarán el resto del proceso.
Output F1: Mapa PESTEL con impacto/probabilidad por dimensión, supuestos estratégicos clave, 3-5 megatendencias.

FASE 2 — ANÁLISIS DE LA INDUSTRIA — 3 secciones:
2.1 Segmentación de la Industria: Cómo está segmentada la industria (por producto, canal, geografía, tipo de consumidor). Esta segmentación es la base para identificar los mercados objetivo de F3.
2.2 Dinámica Estructural — 5 Fuerzas de Porter: Rivalidad competitiva (concentración, diferenciación, crecimiento, barreras de salida), Amenaza de nuevos entrantes (barreras de entrada: escala, canales, capital, regulación, marca), Poder de proveedores (concentración, diferenciación de insumos, costo de cambio), Poder de compradores/canales (concentración del canal, sensibilidad precio, importancia producto), Amenaza de sustitutos (alternativas disponibles, precio/desempeño, propensión a sustituir). Calificar cada fuerza: Alta / Media / Baja.
2.3 Tendencias y Dinámicas de la Industria: Tendencias que redefinen la industria. Incluir horizonte temporal.
Output F2: Mapa de segmentación, análisis Porter con intensidad, tendencias, atractivo general.

FASE 3 — IDENTIFICACIÓN Y PRIORIZACIÓN DE MERCADOS — 2 secciones:
3.1 Inventario de Mercados: Listar todos los mercados actuales y potenciales. Por cada uno: categoría, geografía, presencia actual (sí/no), relevancia estimada.
3.2 Matriz de Priorización: Evaluar cada mercado en Atractivo vs Posición competitiva. Clasificar en: Prioridad 1 (invertir y crecer), Prioridad 2 (mantener), Prioridad 3 (evaluar salida), Nuevo ingreso.
Output F3: Lista priorizada, matriz atractivo vs posición, alcance del análisis por mercado.

════ NIVEL UNIDAD DE NEGOCIO (se repite para cada mercado P1) ════

ETAPA 1 — ANÁLISIS DEL MERCADO — 5 secciones:
1.1 Tamaño y valor total: Valor en $ y unidades. Datos históricos 3-5 años.
1.2 Tasa de crecimiento (CAGR): CAGR histórico y proyección 3-5 años.
1.3 Estacionalidad: Picos y valles de demanda.
1.4 Sub-segmentos: Sub-segmentos internos por precio, ocasión de uso, canal.
1.5 Rentabilidad del sector: Márgenes promedio del sector como referencia.

ETAPA 2 — ANÁLISIS DEL CONSUMIDOR — 6 secciones:
2.1 Segmentación del consumidor: Grupos con necesidades distintas. Perfil con tamaño estimado y atractivo.
2.2 Necesidad insatisfecha: ¿Qué problema/deseo/tensión existe? ¿Funcional, emocional o social?
2.3 Entrada a la categoría: ¿Cómo y por qué el consumidor comienza a usar esta categoría? Detonadores.
2.4 Adopción y cambio de marca: ¿Cómo selecciona una marca? ¿Qué lo hace cambiar? Barreras.
2.5 Momentos de consumo: ¿Cuándo, dónde y con qué frecuencia consume?
2.6 Proceso de compra: ¿Dónde compra (físico/digital)? ¿Quién influye?

ETAPA 3 — ANÁLISIS COMPETITIVO — 3 secciones:
3.1 Ficha por competidor (3-5 directos): posicionamiento, portafolio, precio, canales, comunicación, market share.
3.2 Mapa de posicionamiento: Cómo se ubican todos los actores.
3.3 Blancos estratégicos: Espacios no ocupados que podrían ser oportunidades.

ETAPA 4 — ANÁLISIS INTERNO — 3 secciones:
4.1 Variables competitivas: Posicionamiento actual, portafolio, precio, canales, comunicación, resultados.
4.2 Capacidades organizacionales: Talento, procesos, tecnología/datos, capacidad financiera, cultura, supply chain.
4.3 Opciones estratégicas: 2-4 rutas distintas evaluadas en coherencia, atractivo, factibilidad, riesgo, sostenibilidad.

ETAPA 5 — DEFINICIÓN DE ESTRATEGIA POR MERCADO — 7 secciones:
5.1 Posicionamiento ideal: Propuesta de valor central, territorio de marca, segmento objetivo.
5.2 Portafolio: Arquitectura ideal. ¿Qué se potencia, reposiciona, descontinúa, lanza?
5.3 Estrategia de precio: Nivel, arquitectura entre referencias, política de descuentos.
5.4 Canales: Prioridad por canal, métricas de distribución y ejecución en PDV.
5.5 Comunicación: Mensaje central, medios, mezcla marca/activación, estrategia digital.
5.6 Objetivos y pilares: Objetivos a 3-5 años + pilares estratégicos con iniciativas tácticas.
5.7 Escenarios estratégicos: Escenario base, optimista y de contingencia.

════ CONSOLIDACIÓN CORPORATIVA ════

FASE 6 — ESTRATEGIA CORPORATIVA — 5 secciones:
6.1 Asignación de recursos entre mercados: Presupuesto explícito por mercado según priorización.
6.2 Sinergias: Portafolio, canal, comunicación y operacional entre mercados.
6.3 Narrativa corporativa: ¿Dónde jugar? / ¿Cómo ganar? / ¿Con qué capacidades?
6.4 Mapa de stakeholders: Accionistas, directivos, comercial, canales, reguladores.
6.5 Cuadro de mando (KPIs): Métricas de negocio, marca y capacidades consolidadas.

════ REGLAS ════
1. Haz UNA sola pregunta a la vez.
2. Confirma brevemente cada respuesta antes de avanzar.
3. Si la respuesta es vaga, pide clarificación.
4. Al completar cada sección marca "✅ [Nombre] completada" y avanza.
5. Al completar una Fase/Etapa presenta un resumen y confirma antes de avanzar.
6. Señala hallazgos estratégicamente importantes cuando aparezcan.
7. Al comenzar cada Fase/Etapa explica en 1 línea qué se hará y por qué importa.
8. Al pasar de Fases globales a UdN, pregunta cuántas UdN hay y cuáles son.

INICIO: Preséntate brevemente como el Agente Estratégico NVC y pide el nombre de la empresa cliente y la industria.`

const PHASE_ORDER = [
  'init',
  'f1','f1-1','f1-2','f1-3','f1-4','f1-5','f1-6','f1-7',
  'f2','f2-1','f2-2','f2-3',
  'f3','f3-1','f3-2',
  'bu',
  'e1','e1-1','e1-2','e1-3','e1-4','e1-5',
  'e2','e2-1','e2-2','e2-3','e2-4','e2-5','e2-6',
  'e3','e3-1','e3-2','e3-3',
  'e4','e4-1','e4-2','e4-3',
  'e5','e5-1','e5-2','e5-3','e5-4','e5-5','e5-6','e5-7',
  'f6','f6-1','f6-2','f6-3','f6-4','f6-5'
]

const PHASE_NAMES = {
  'init':'Inicio',
  'f1':'F1 · Análisis Macro (PESTEL)','f1-1':'Análisis Político','f1-2':'Análisis Económico','f1-3':'Análisis Social','f1-4':'Análisis Tecnológico','f1-5':'Análisis Ambiental/ESG','f1-6':'Análisis Legal','f1-7':'Supuestos y Megatendencias',
  'f2':'F2 · Análisis de la Industria','f2-1':'Segmentación','f2-2':'5 Fuerzas de Porter','f2-3':'Tendencias de la Industria',
  'f3':'F3 · Mercados Objetivo','f3-1':'Inventario de Mercados','f3-2':'Matriz de Priorización',
  'bu':'Definición de Unidades de Negocio',
  'e1':'E1 · Análisis del Mercado','e1-1':'Tamaño y valor','e1-2':'CAGR','e1-3':'Estacionalidad','e1-4':'Sub-segmentos','e1-5':'Rentabilidad del sector',
  'e2':'E2 · Análisis del Consumidor','e2-1':'Segmentación del consumidor','e2-2':'Necesidad insatisfecha','e2-3':'Entrada a la categoría','e2-4':'Adopción y cambio de marca','e2-5':'Momentos de consumo','e2-6':'Proceso de compra',
  'e3':'E3 · Análisis Competitivo','e3-1':'Ficha por competidor','e3-2':'Mapa de posicionamiento','e3-3':'Blancos estratégicos',
  'e4':'E4 · Análisis Interno','e4-1':'Variables competitivas','e4-2':'Capacidades organizacionales','e4-3':'Opciones estratégicas',
  'e5':'E5 · Estrategia por Mercado','e5-1':'Posicionamiento ideal','e5-2':'Portafolio','e5-3':'Precio','e5-4':'Canales','e5-5':'Comunicación','e5-6':'Objetivos y pilares','e5-7':'Escenarios',
  'f6':'F6 · Estrategia Corporativa','f6-1':'Asignación de recursos','f6-2':'Sinergias','f6-3':'Narrativa corporativa','f6-4':'Stakeholders','f6-5':'Cuadro de mando (KPIs)'
}

const SIDEBAR_ITEMS = [
  { id: 'init', label: 'Inicio', level: 0 },
  { id: 'f1', label: 'F1 · Análisis Macro (PESTEL)', level: 0 },
  { id: 'f1-1', label: 'Análisis Político', level: 1 },
  { id: 'f1-2', label: 'Análisis Económico', level: 1 },
  { id: 'f1-3', label: 'Análisis Social', level: 1 },
  { id: 'f1-4', label: 'Análisis Tecnológico', level: 1 },
  { id: 'f1-5', label: 'Análisis Ambiental/ESG', level: 1 },
  { id: 'f1-6', label: 'Análisis Legal', level: 1 },
  { id: 'f1-7', label: 'Supuestos y Megatendencias', level: 1 },
  { id: 'f2', label: 'F2 · Análisis de la Industria', level: 0 },
  { id: 'f2-1', label: 'Segmentación', level: 1 },
  { id: 'f2-2', label: '5 Fuerzas de Porter', level: 1 },
  { id: 'f2-3', label: 'Tendencias de la Industria', level: 1 },
  { id: 'f3', label: 'F3 · Mercados Objetivo', level: 0 },
  { id: 'f3-1', label: 'Inventario de Mercados', level: 1 },
  { id: 'f3-2', label: 'Matriz de Priorización', level: 1 },
  { id: 'bu', label: 'Definición de UdN', level: 0, divider: 'Por Unidad de Negocio' },
  { id: 'e1', label: 'E1 · Análisis del Mercado', level: 0 },
  { id: 'e1-1', label: 'Tamaño y valor', level: 1 },
  { id: 'e1-2', label: 'CAGR', level: 1 },
  { id: 'e1-3', label: 'Estacionalidad', level: 1 },
  { id: 'e1-4', label: 'Sub-segmentos', level: 1 },
  { id: 'e1-5', label: 'Rentabilidad del sector', level: 1 },
  { id: 'e2', label: 'E2 · Análisis del Consumidor', level: 0 },
  { id: 'e2-1', label: 'Segmentación', level: 1 },
  { id: 'e2-2', label: 'Necesidad insatisfecha', level: 1 },
  { id: 'e2-3', label: 'Entrada a la categoría', level: 1 },
  { id: 'e2-4', label: 'Adopción y cambio de marca', level: 1 },
  { id: 'e2-5', label: 'Momentos de consumo', level: 1 },
  { id: 'e2-6', label: 'Proceso de compra', level: 1 },
  { id: 'e3', label: 'E3 · Análisis Competitivo', level: 0 },
  { id: 'e3-1', label: 'Ficha por competidor', level: 1 },
  { id: 'e3-2', label: 'Mapa de posicionamiento', level: 1 },
  { id: 'e3-3', label: 'Blancos estratégicos', level: 1 },
  { id: 'e4', label: 'E4 · Análisis Interno', level: 0 },
  { id: 'e4-1', label: 'Variables competitivas', level: 1 },
  { id: 'e4-2', label: 'Capacidades organizacionales', level: 1 },
  { id: 'e4-3', label: 'Opciones estratégicas', level: 1 },
  { id: 'e5', label: 'E5 · Estrategia por Mercado', level: 0 },
  { id: 'e5-1', label: 'Posicionamiento ideal', level: 1 },
  { id: 'e5-2', label: 'Portafolio', level: 1 },
  { id: 'e5-3', label: 'Precio', level: 1 },
  { id: 'e5-4', label: 'Canales', level: 1 },
  { id: 'e5-5', label: 'Comunicación', level: 1 },
  { id: 'e5-6', label: 'Objetivos y pilares', level: 1 },
  { id: 'e5-7', label: 'Escenarios', level: 1 },
  { id: 'f6', label: 'F6 · Estrategia Corporativa', level: 0, divider: 'Consolidación' },
  { id: 'f6-1', label: 'Asignación de recursos', level: 1 },
  { id: 'f6-2', label: 'Sinergias', level: 1 },
  { id: 'f6-3', label: 'Narrativa corporativa', level: 1 },
  { id: 'f6-4', label: 'Stakeholders', level: 1 },
  { id: 'f6-5', label: 'Cuadro de mando (KPIs)', level: 1 },
]

function detectPhase(text) {
  const t = text.toLowerCase()
  if (t.includes('análisis político') || t.includes('1.1')) return 'f1-1'
  if (t.includes('análisis económico') || t.includes('1.2')) return 'f1-2'
  if (t.includes('análisis social') || t.includes('1.3')) return 'f1-3'
  if (t.includes('análisis tecnológico') || t.includes('1.4')) return 'f1-4'
  if (t.includes('ambiental') || t.includes('esg') || t.includes('1.5')) return 'f1-5'
  if (t.includes('análisis legal') || t.includes('1.6')) return 'f1-6'
  if (t.includes('supuestos') || t.includes('megatendencias') || t.includes('1.7')) return 'f1-7'
  if (t.includes('segmentación de la industria') || t.includes('2.1')) return 'f2-1'
  if (t.includes('porter') || t.includes('fuerzas') || t.includes('2.2')) return 'f2-2'
  if ((t.includes('tendencias') && t.includes('industria')) || t.includes('2.3')) return 'f2-3'
  if (t.includes('inventario de mercados') || t.includes('3.1')) return 'f3-1'
  if (t.includes('matriz de priorización') || t.includes('3.2')) return 'f3-2'
  if (t.includes('unidad') && t.includes('negocio')) return 'bu'
  if (t.includes('tamaño') && t.includes('valor')) return 'e1-1'
  if (t.includes('cagr') || t.includes('tasa de crecimiento')) return 'e1-2'
  if (t.includes('estacionalidad')) return 'e1-3'
  if (t.includes('sub-segmento')) return 'e1-4'
  if (t.includes('rentabilidad del sector')) return 'e1-5'
  if (t.includes('segmentación del consumidor')) return 'e2-1'
  if (t.includes('necesidad insatisfecha')) return 'e2-2'
  if (t.includes('entrada a la categoría')) return 'e2-3'
  if (t.includes('adopción') || t.includes('cambio de marca')) return 'e2-4'
  if (t.includes('momentos de consumo')) return 'e2-5'
  if (t.includes('proceso de compra')) return 'e2-6'
  if (t.includes('ficha') && t.includes('competidor')) return 'e3-1'
  if (t.includes('mapa de posicionamiento')) return 'e3-2'
  if (t.includes('blancos estratégicos')) return 'e3-3'
  if (t.includes('variables competitivas') && t.includes('interno')) return 'e4-1'
  if (t.includes('capacidades organizacionales')) return 'e4-2'
  if (t.includes('opciones estratégicas')) return 'e4-3'
  if (t.includes('posicionamiento ideal')) return 'e5-1'
  if (t.includes('portafolio') && t.includes('arquitectura')) return 'e5-2'
  if (t.includes('estrategia de precio')) return 'e5-3'
  if (t.includes('canales') && t.includes('llegará')) return 'e5-4'
  if (t.includes('comunicación') && t.includes('mensaje central')) return 'e5-5'
  if (t.includes('objetivos') && t.includes('pilares')) return 'e5-6'
  if (t.includes('escenarios') && t.includes('estratégi')) return 'e5-7'
  if (t.includes('asignación de recursos')) return 'f6-1'
  if (t.includes('sinergias')) return 'f6-2'
  if (t.includes('narrativa corporativa') || t.includes('dónde jugar')) return 'f6-3'
  if (t.includes('stakeholders')) return 'f6-4'
  if (t.includes('cuadro de mando') || t.includes('kpis')) return 'f6-5'
  if (t.includes('fase 1') || t.includes('pestel') || t.includes('análisis macro')) return 'f1'
  if (t.includes('fase 2') || t.includes('análisis de la industria')) return 'f2'
  if (t.includes('fase 3') || t.includes('mercados objetivo')) return 'f3'
  if (t.includes('etapa 1') || t.includes('análisis del mercado')) return 'e1'
  if (t.includes('etapa 2') || t.includes('análisis del consumidor')) return 'e2'
  if (t.includes('etapa 3') || t.includes('análisis competitivo')) return 'e3'
  if (t.includes('etapa 4') || t.includes('análisis interno')) return 'e4'
  if (t.includes('etapa 5') || t.includes('definición de estrategia')) return 'e5'
  if (t.includes('fase 6') || t.includes('consolidación corporativa')) return 'f6'
  return null
}

const NVC_PHASES_TEMPLATE = [
  { key: 'F1', name: 'F1 — Análisis Macro (PESTEL)', order: 0, sections: [
    { name: '1.1 Análisis Político', tasks: ['Mapear regulaciones vigentes','Evaluar estabilidad política','Identificar leyes de competencia e incentivos'] },
    { name: '1.2 Análisis Económico', tasks: ['Analizar indicadores macroeconómicos','Evaluar poder adquisitivo y ciclos','Identificar riesgos económicos'] },
    { name: '1.3 Análisis Social', tasks: ['Mapear cambios demográficos','Analizar tendencias de consumo','Identificar valores y estilos de vida'] },
    { name: '1.4 Análisis Tecnológico', tasks: ['Identificar innovaciones disruptivas','Evaluar digitalización del canal','Analizar impacto e-commerce'] },
    { name: '1.5 Análisis Ambiental/ESG', tasks: ['Mapear regulaciones ambientales','Analizar tendencias de sostenibilidad','Evaluar expectativas RSE'] },
    { name: '1.6 Análisis Legal', tasks: ['Revisar normativas de etiquetado y publicidad','Evaluar protección al consumidor','Identificar riesgos legales'] },
    { name: '1.7 Supuestos y Megatendencias', tasks: ['Consolidar megatendencias PESTEL','Definir supuestos estratégicos'] },
  ]},
  { key: 'F2', name: 'F2 — Análisis de la Industria', order: 1, sections: [
    { name: '2.1 Segmentación de la Industria', tasks: ['Mapear segmentación por producto/canal/geografía','Identificar sub-segmentos clave'] },
    { name: '2.2 Dinámica Estructural (5 Fuerzas)', tasks: ['Analizar rivalidad competitiva','Evaluar nuevos entrantes','Analizar poder proveedores','Analizar poder compradores','Evaluar amenaza sustitutos'] },
    { name: '2.3 Tendencias de la Industria', tasks: ['Identificar tendencias redefiniendoras','Proyectar horizonte temporal'] },
  ]},
  { key: 'F3', name: 'F3 — Identificación y Priorización de Mercados', order: 2, sections: [
    { name: '3.1 Inventario de Mercados', tasks: ['Listar mercados actuales','Listar mercados potenciales','Documentar presencia y relevancia'] },
    { name: '3.2 Matriz de Priorización', tasks: ['Evaluar atractivo por mercado','Evaluar posición competitiva','Clasificar mercados P1/P2/P3'] },
  ]},
  { key: 'F6', name: 'F6 — Estrategia Corporativa', order: 10, sections: [
    { name: '6.1 Asignación de Recursos', tasks: ['Definir presupuesto por mercado','Justificar asignación'] },
    { name: '6.2 Sinergias', tasks: ['Identificar sinergias de portafolio','Identificar sinergias operacionales'] },
    { name: '6.3 Narrativa Corporativa', tasks: ['Definir dónde jugar','Definir cómo ganar','Definir capacidades necesarias'] },
    { name: '6.4 Mapa de Stakeholders', tasks: ['Mapear stakeholders internos','Mapear stakeholders externos'] },
    { name: '6.5 Cuadro de Mando (KPIs)', tasks: ['Definir KPIs de negocio','Definir KPIs de marca','Definir KPIs de capacidades'] },
  ]},
]

const NVC_BU_PHASES_TEMPLATE = [
  { key: 'E1', name: 'E1 — Análisis del Mercado', order: 0, sections: [
    { name: '1.1 Tamaño y Valor Total', tasks: ['Documentar valor en $ y unidades','Recopilar datos históricos 3-5 años'] },
    { name: '1.2 Tasa de Crecimiento (CAGR)', tasks: ['Calcular CAGR histórico','Proyectar crecimiento 3-5 años'] },
    { name: '1.3 Estacionalidad', tasks: ['Identificar picos de demanda','Identificar valles de demanda'] },
    { name: '1.4 Sub-segmentos', tasks: ['Mapear sub-segmentos por precio','Mapear sub-segmentos por canal'] },
    { name: '1.5 Rentabilidad del Sector', tasks: ['Documentar márgenes promedio del sector'] },
  ]},
  { key: 'E2', name: 'E2 — Análisis del Consumidor', order: 1, sections: [
    { name: '2.1 Segmentación del Consumidor', tasks: ['Identificar segmentos con necesidades distintas','Perfilar tamaño y atractivo'] },
    { name: '2.2 Necesidad Insatisfecha', tasks: ['Identificar problema/deseo/tensión','Clasificar necesidad funcional/emocional/social'] },
    { name: '2.3 Entrada a la Categoría', tasks: ['Mapear detonadores de entrada','Documentar adopción inicial'] },
    { name: '2.4 Adopción y Cambio de Marca', tasks: ['Analizar criterios de selección','Identificar barreras al cambio'] },
    { name: '2.5 Momentos de Consumo', tasks: ['Documentar cuándo y dónde consume','Documentar frecuencia'] },
    { name: '2.6 Proceso de Compra', tasks: ['Mapear canales de compra','Identificar influenciadores'] },
  ]},
  { key: 'E3', name: 'E3 — Análisis Competitivo', order: 2, sections: [
    { name: '3.1 Ficha por Competidor', tasks: ['Documentar 3-5 competidores directos','Analizar posicionamiento y market share'] },
    { name: '3.2 Mapa de Posicionamiento', tasks: ['Construir mapa perceptual de competidores'] },
    { name: '3.3 Blancos Estratégicos', tasks: ['Identificar espacios no ocupados','Documentar oportunidades'] },
  ]},
  { key: 'E4', name: 'E4 — Análisis Interno', order: 3, sections: [
    { name: '4.1 Variables Competitivas', tasks: ['Documentar posicionamiento actual','Analizar portafolio precio canales comunicación'] },
    { name: '4.2 Capacidades Organizacionales', tasks: ['Evaluar talento y procesos','Evaluar tecnología y capacidad financiera'] },
    { name: '4.3 Opciones Estratégicas', tasks: ['Formular 2-4 opciones estratégicas','Evaluar coherencia atractivo factibilidad'] },
  ]},
  { key: 'E5', name: 'E5 — Estrategia por Mercado', order: 4, sections: [
    { name: '5.1 Posicionamiento Ideal', tasks: ['Definir propuesta de valor central','Definir segmento objetivo'] },
    { name: '5.2 Portafolio', tasks: ['Definir arquitectura de portafolio','Decidir qué potenciar/lanzar/descontinuar'] },
    { name: '5.3 Estrategia de Precio', tasks: ['Definir nivel de precio','Definir política de descuentos'] },
    { name: '5.4 Canales', tasks: ['Priorizar canales','Definir métricas de distribución'] },
    { name: '5.5 Comunicación', tasks: ['Definir mensaje central','Definir mezcla de medios'] },
    { name: '5.6 Objetivos y Pilares', tasks: ['Definir objetivos 3-5 años','Definir pilares e iniciativas'] },
    { name: '5.7 Escenarios', tasks: ['Construir escenario base','Construir escenarios optimista y contingencia'] },
  ]},
]

function buildSystemPrompt(projectData) {
  let prompt = SYSTEM_PROMPT

  if (!projectData) return prompt

  const { consultancy, phases, reports, bus } = projectData

  prompt += `\n\n════ CONTEXTO DEL PROYECTO ACTIVO ════\n`
  prompt += `Empresa cliente: ${consultancy.client_company || consultancy.name}\n`
  prompt += `Industria: ${consultancy.industry || 'No especificada'}\n`
  if (consultancy.country) prompt += `País(es): ${consultancy.country}\n`
  if (consultancy.company_size) prompt += `Tamaño: ${consultancy.company_size}\n`
  if (consultancy.years_in_market) prompt += `Años en el mercado: ${consultancy.years_in_market}\n`
  if (consultancy.annual_revenue) prompt += `Ventas anuales: ${consultancy.annual_revenue}\n`
  if (consultancy.main_products) prompt += `Portafolio: ${consultancy.main_products}\n`
  prompt += `Estado proyecto: ${consultancy.status}\n`

  if (phases?.length) {
    prompt += `\nFASES EXISTENTES EN LA APP:\n`
    for (const phase of phases) {
      const buLabel = phase.business_unit_id ? ` [UdN: ${bus?.find(b=>b.id===phase.business_unit_id)?.name || 'BU'}]` : ' [Nivel consultoría]'
      const sections = phase.sections || []
      const totalTasks = sections.reduce((acc, s) => acc + (s.tasks?.length || 0), 0)
      const doneTasks = sections.reduce((acc, s) => acc + (s.tasks?.filter(t=>t.status==='completed').length || 0), 0)
      const pct = totalTasks > 0 ? Math.round((doneTasks/totalTasks)*100) : 0
      prompt += `• ${phase.name}${buLabel} — ${pct}% completado (${doneTasks}/${totalTasks} tareas)\n`
    }
  } else {
    prompt += `\nNo hay fases creadas aún para este proyecto.\n`
  }

  if (reports?.length) {
    prompt += `\nINFORMES YA GENERADOS: ${reports.length} informe(s) guardados.\n`
  }

  prompt += `\n════ REGLAS ADICIONALES CON PROYECTO ACTIVO ════\n`
  prompt += `1. Al iniciar, presenta el estado del proyecto y pregunta por cuál fase/etapa comenzar.\n`
  prompt += `2. Cuando una sección esté completa, incluye "✅ [nombre] lista para guardar" y pregunta si se genera el informe.\n`
  prompt += `3. Cuando el usuario confirme guardar, incluye exactamente "💾 GUARDAR_INFORME:[nombre de la fase/sección]" en tu respuesta.\n`
  prompt += `4. Cuando el usuario confirme crear una fase nueva, incluye "📋 CREAR_FASE:[nombre exacto de la fase NVC]" en tu respuesta.\n`
  prompt += `5. Adapta las preguntas al contexto de ${consultancy.name} en su industria.\n`

  return prompt
}

function parseAgentCommands(text) {
  const commands = []

  const saveMatch = text.match(/💾 GUARDAR_INFORME:([^\n]+)/g)
  if (saveMatch) {
    saveMatch.forEach(m => {
      const phaseName = m.replace('💾 GUARDAR_INFORME:', '').trim()
      commands.push({ type: 'save_report', phaseName })
    })
  }

  const createMatch = text.match(/📋 CREAR_FASE:([^\n]+)/g)
  if (createMatch) {
    createMatch.forEach(m => {
      const phaseName = m.replace('📋 CREAR_FASE:', '').trim()
      commands.push({ type: 'create_phase', phaseName })
    })
  }

  const cleanText = text
    .replace(/💾 GUARDAR_INFORME:[^\n]+/g, '')
    .replace(/📋 CREAR_FASE:[^\n]+/g, '')
    .trim()

  return { cleanText, commands }
}

async function createNVCPhase(consultancyId, phaseTemplate, buId = null) {
  const { data: phase, error: phaseErr } = await supabase
    .from('phases')
    .insert({ consultancy_id: consultancyId, business_unit_id: buId, name: phaseTemplate.name, phase_order: phaseTemplate.order })
    .select()
    .single()
  if (phaseErr || !phase) return null

  for (let si = 0; si < phaseTemplate.sections.length; si++) {
    const sectionTemplate = phaseTemplate.sections[si]
    const { data: section } = await supabase
      .from('sections')
      .insert({ phase_id: phase.id, name: sectionTemplate.name, section_order: si })
      .select()
      .single()
    if (!section) continue

    for (let ti = 0; ti < sectionTemplate.tasks.length; ti++) {
      await supabase.from('tasks').insert({
        section_id: section.id,
        name: sectionTemplate.tasks[ti],
        status: 'pending',
        task_order: ti,
      })
    }
  }
  return phase
}

async function saveReportToPhase(consultancyId, phaseId, content, reportType, userId) {
  const { data, error } = await supabase.from('ai_reports').insert({
    consultancy_id: consultancyId,
    entity_type: 'phase',
    entity_id: phaseId,
    report_type: reportType || 'agent_generated',
    content,
    generated_by: userId,
  }).select().single()
  return { data, error }
}

export default function AgenteEstrategico() {
  const { profile } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { markCurrentAnswered, currentQuestion, activeConsultancyId, setIsProcessing } = useAutonomousAgent()

  // URL params for autonomous mode
  const autonomousProjectId = searchParams.get('projectId')
  const autonomousQuestionId = searchParams.get('questionId')
  const isAutonomousMode = searchParams.get('auto') === 'true' && !!autonomousProjectId
  const autonomousQuestion = AUTONOMOUS_QUESTIONS.find(q => q.id === autonomousQuestionId) || null

  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState('Pensando...')
  const [currentPhase, setCurrentPhase] = useState('init')
  const [completedPhases, setCompletedPhases] = useState([])
  const [empresa, setEmpresa] = useState('')
  const [industria, setIndustria] = useState('')
  const [exchanges, setExchanges] = useState(0)
  const [quickReplies, setQuickReplies] = useState([])
  const [searchEnabled, setSearchEnabled] = useState(false)
  const [uploadedDocs, setUploadedDocs] = useState([])
  const [showDropzone, setShowDropzone] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [projectData, setProjectData] = useState(null)
  const [loadingProject, setLoadingProject] = useState(false)
  const [pendingSave, setPendingSave] = useState(null)
  const [consultancies, setConsultancies] = useState([])
  const [historyRestored, setHistoryRestored] = useState(false)
  // Streaming simulation
  const [streamingText, setStreamingText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const streamRef = useRef(null)
  // Sessions management
  const [sessions, setSessions] = useState([])
  const [showSessions, setShowSessions] = useState(false)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)
  const historyRef = useRef([])
  const convRecordIdRef = useRef(null)
  const saveTimerRef = useRef(null)

  useEffect(() => {
    loadConsultancies()
  }, [])

  // Autonomous mode: auto-select project when URL params are set
  useEffect(() => {
    if (!isAutonomousMode || !autonomousProjectId || consultancies.length === 0) return
    if (selectedProject !== null) return // already selected

    const targetConsultancy = consultancies.find(c => c.id === autonomousProjectId)
    if (targetConsultancy) {
      startWithProjectAutonomous(targetConsultancy, autonomousQuestion)
    }
  }, [isAutonomousMode, autonomousProjectId, consultancies, autonomousQuestion])

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  async function loadConsultancies() {
    try {
      const { data, error } = await supabase
        .from('consultancies')
        .select('id, name, client_company, status')
        .order('created_at', { ascending: false })
      if (!error && data) {
        setConsultancies(data)
      }
    } catch (e) {
      console.error('Error loading consultancies:', e)
    }
  }

  // ── Streaming simulation ──────────────────────────────────────────────────
  function streamText(fullText, onComplete) {
    if (streamRef.current) clearInterval(streamRef.current)
    const tokens = fullText.split(/(\s+)/)   // split preserving spaces
    let i = 0
    setIsStreaming(true)
    setStreamingText('')

    streamRef.current = setInterval(() => {
      i = Math.min(i + 10, tokens.length)    // reveal ~10 tokens per tick
      setStreamingText(tokens.slice(0, i).join(''))
      if (i >= tokens.length) {
        clearInterval(streamRef.current)
        setIsStreaming(false)
        setStreamingText('')
        onComplete()
      }
    }, 18) // ~50 fps feels fluid
  }

  // ── Session management ────────────────────────────────────────────────────
  async function loadAllSessions(consultancyId) {
    try {
      const { data } = await supabase
        .from('ai_reports')
        .select('id, content, created_at')
        .eq('consultancy_id', consultancyId)
        .eq('report_type', 'conversation_history')
        .order('created_at', { ascending: false })
        .limit(20)
      setSessions(data || [])
    } catch (e) {
      console.error('Error loading sessions:', e)
    }
  }

  async function switchSession(record) {
    if (streamRef.current) clearInterval(streamRef.current)
    setIsStreaming(false)
    setStreamingText('')
    try {
      const stored = JSON.parse(record.content)
      convRecordIdRef.current = record.id
      historyRef.current = stored.history || []
      const msgs = stored.messages || []
      setMessages([
        { role: 'system', text: `💬 Sesión del ${new Date(record.created_at).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })} — ${msgs.filter(m => m.role !== 'system').length} mensajes` },
        ...msgs,
      ])
      setHistoryRestored(true)
      setShowSessions(false)
      const lastA = [...historyRef.current].reverse().find(m => m.role === 'assistant')
      if (lastA) { const d = detectPhase(lastA.content); if (d) setCurrentPhase(d) }
    } catch (e) { console.error('Error switching session:', e) }
  }

  // ── Auto-complete tasks when agent marks section ✅ ────────────────────────
  async function autoMarkSectionsComplete(agentText) {
    if (!projectData?.phases?.length) return

    // Match "✅ [section name] completada/lista..."
    const pattern = /✅\s*(.{3,80?}?)(?:\s+(?:completada?|lista(?:\s+para\s+guardar)?|listo?))/gi
    let m
    const matched = []
    while ((m = pattern.exec(agentText)) !== null) {
      matched.push(m[1].trim().toLowerCase())
    }
    if (!matched.length) return

    let didUpdate = false
    for (const phase of projectData.phases) {
      for (const section of (phase.sections || [])) {
        const sn = section.name.toLowerCase()
        const isMatch = matched.some(label => {
          const words = label.split(/\s+/).filter(w => w.length > 3)
          return words.length > 0 && words.filter(w => sn.includes(w)).length >= Math.ceil(words.length * 0.5)
        })
        if (isMatch && section.tasks?.length) {
          // Mark all tasks in this section as completed
          const taskIds = section.tasks.map(t => t.id)
          await supabase.from('tasks').update({ status: 'completed' }).in('id', taskIds)
          didUpdate = true
        }
      }
      if (didUpdate) {
        // Recalculate phase progress
        const { data: sections } = await supabase
          .from('sections')
          .select('tasks(status)')
          .eq('phase_id', phase.id)
        if (sections) {
          const allTasks = sections.flatMap(s => s.tasks || [])
          const done = allTasks.filter(t => t.status === 'completed').length
          const pct = allTasks.length ? Math.round((done / allTasks.length) * 100) : 0
          await supabase.from('phases').update({ progress: pct }).eq('id', phase.id)
        }
      }
    }
    // If updated, silently refresh phases in projectData
    if (didUpdate) {
      const { data: newPhases } = await supabase
        .from('phases')
        .select('id, name, phase_order, business_unit_id, sections(id, name, section_order, tasks(id, name, status))')
        .eq('consultancy_id', projectData.consultancy.id)
        .order('phase_order')
      if (newPhases) setProjectData(pd => ({ ...pd, phases: newPhases }))
    }
  }

  // ── Persistencia de conversación ─────────────────────────────────────────
  async function loadConversationHistory(consultancyId) {
    try {
      const { data } = await supabase
        .from('ai_reports')
        .select('id, content')
        .eq('consultancy_id', consultancyId)
        .eq('report_type', 'conversation_history')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (data?.content) {
        const stored = JSON.parse(data.content)
        convRecordIdRef.current = data.id
        return stored // { messages: [...], history: [...] }
      }
    } catch (e) {
      console.error('Error loading conversation history:', e)
    }
    return null
  }

  async function saveConversationHistory(consultancyId, msgs, hist) {
    if (!consultancyId) return
    try {
      const content = JSON.stringify({ messages: msgs, history: hist })
      if (convRecordIdRef.current) {
        const { error } = await supabase
          .from('ai_reports')
          .update({ content, updated_at: new Date().toISOString() })
          .eq('id', convRecordIdRef.current)
        if (error) console.error('Save update failed:', error.message, error.details)
      } else {
        const { data, error } = await supabase
          .from('ai_reports')
          .insert({
            consultancy_id: consultancyId,
            entity_type: 'consultancy',
            entity_id: consultancyId,
            report_type: 'conversation_history',
            content,
            generated_by: profile?.id,
          })
          .select('id')
          .single()
        if (error) console.error('Save insert failed:', error.message, error.details)
        if (data?.id) convRecordIdRef.current = data.id
      }
    } catch (e) {
      console.error('Error saving conversation history:', e)
    }
  }

  function scheduleSave(consultancyId, msgs, hist) {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveConversationHistory(consultancyId, msgs, hist)
    }, 1500) // guarda 1.5s después del último mensaje
  }

  // ── Autonomous mode: start with project + question context ────────────────
  async function startWithProjectAutonomous(consultancy, question) {
    setSelectedProject(consultancy)
    setLoadingProject(true)
    convRecordIdRef.current = null
    setHistoryRestored(false)

    try {
      const [phasesRes, reportsRes, busRes] = await Promise.all([
        supabase.from('phases').select('id, name, phase_order, business_unit_id, sections(id, name, section_order, tasks(id, name, status))').eq('consultancy_id', consultancy.id).order('phase_order'),
        supabase.from('ai_reports').select('id, entity_type, entity_id, report_type, created_at').eq('consultancy_id', consultancy.id).not('report_type', 'in', '("conversation_history","agent_queue")').limit(20),
        supabase.from('business_units').select('id, name').eq('consultancy_id', consultancy.id),
      ])
      loadAllSessions(consultancy.id)

      const pd = {
        consultancy,
        phases: phasesRes.data || [],
        reports: reportsRes.data || [],
        bus: busRes.data || [],
      }
      setProjectData(pd)
      setLoadingProject(false)

      if (!question) {
        // Fallback to normal start
        await startWithProject(consultancy)
        return
      }

      const empresa_ = consultancy.client_company || consultancy.name
      const industria_ = consultancy.industry || 'su industria'
      const pais_ = consultancy.country || 'su mercado principal'
      const tamaño_ = consultancy.company_size || ''
      const años_ = consultancy.years_in_market ? `${consultancy.years_in_market} años en el mercado` : ''
      const ventas_ = consultancy.annual_revenue || ''
      const portafolio_ = consultancy.main_products || ''

      // All questions are sequential — the agent asks and waits for the user's response
      setMessages([{
        role: 'system',
        text: `🎯 **Pregunta ${question.priority} de ${AUTONOMOUS_QUESTIONS.length}** — ${question.section} · Proyecto: ${empresa_}`
      }])

      // Show agent question immediately
      const agentGreeting = question.agentPrompt
      finalizeAgentMessage(agentGreeting, true)
      historyRef.current = [{ role: 'assistant', content: agentGreeting }]
    } catch (e) {
      setLoadingProject(false)
      console.error('Error in autonomous start:', e)
    }
  }

  // ── Called when user sends a message in autonomous mode after question answered ──
  async function handleAutonomousAnswer(userMsg, pd) {
    const { reply } = await callAgentWithData(userMsg, pd)
    setLoading(false)
    setExchanges(e => e + 1)

    // finalizeAgentMessage handles: conversation save, autoMarkSectionsComplete,
    // and advances the queue via markCurrentAnswered() when ✅ is detected.
    // We do NOT stream in autonomous mode to avoid a race condition where the
    // component unmounts before the debounce timer fires.
    finalizeAgentMessage(reply, false)

    if (reply.includes('✅') && isAutonomousMode) {
      // Auto-save the report immediately — the user won't click "Guardar"
      // because we navigate back to the consultancy page right after.
      const { cleanText, commands } = parseAgentCommands(reply)
      const saveCmd = commands.find(c => c.type === 'save_report')
      // Fall back to the current queue item's section name if the agent
      // didn't emit a GUARDAR_INFORME command (e.g. asked for confirmation first)
      const phaseName = saveCmd?.phaseName || currentQuestion?.section || ''
      if (phaseName) {
        await executeSave({ phaseName, content: cleanText })
      }

      setMessages(prev => [...prev, {
        role: 'system',
        text: `✅ Sección completada. Volviendo al proyecto...`,
      }])
      // Small delay so the user sees the completion message before navigation
      setTimeout(() => {
        if (activeConsultancyId) navigate(`/consultancy/${activeConsultancyId}`)
      }, 2000)
    }
  }

  async function startWithProject(consultancy) {
    setSelectedProject(consultancy)
    setLoadingProject(true)
    convRecordIdRef.current = null
    setHistoryRestored(false)

    try {
      const [phasesRes, reportsRes, busRes, savedHistory] = await Promise.all([
        supabase.from('phases').select('id, name, phase_order, business_unit_id, sections(id, name, section_order, tasks(id, name, status))').eq('consultancy_id', consultancy.id).order('phase_order'),
        supabase.from('ai_reports').select('id, entity_type, entity_id, report_type, created_at').eq('consultancy_id', consultancy.id).not('report_type', 'eq', 'conversation_history').limit(20),
        supabase.from('business_units').select('id, name').eq('consultancy_id', consultancy.id),
        loadConversationHistory(consultancy.id),
      ])
      // Load all sessions in parallel (non-blocking)
      loadAllSessions(consultancy.id)

      const pd = {
        consultancy,
        phases: phasesRes.data || [],
        reports: reportsRes.data || [],
        bus: busRes.data || [],
      }
      setProjectData(pd)

      // ── Restaurar conversación guardada ──────────────────────────
      if (savedHistory?.history?.length > 0) {
        historyRef.current = savedHistory.history
        const restoredMsgs = savedHistory.messages || []
        setMessages([
          { role: 'system', text: `💬 Conversación restaurada — ${restoredMsgs.filter(m => m.role !== 'system').length} mensajes anteriores cargados` },
          ...restoredMsgs,
        ])
        setHistoryRestored(true)
        setLoadingProject(false)

        // Detectar fase actual del historial
        const lastAssistant = [...historyRef.current].reverse().find(m => m.role === 'assistant')
        if (lastAssistant) {
          const detected = detectPhase(lastAssistant.content)
          if (detected) setCurrentPhase(detected)
        }
      } else {
        // Sin historial → arrancar de cero
        setLoadingProject(false)
        setLoading(true)

        // Build explicit context summary so the agent does NOT ask for info it already has
        const c = consultancy
        const ctxLines = [
          c.client_company  && `• Empresa cliente: ${c.client_company}`,
          c.industry        && `• Industria: ${c.industry}`,
          c.country         && `• País(es): ${c.country}`,
          c.company_size    && `• Tamaño de empresa: ${c.company_size}`,
          c.years_in_market && `• Años en el mercado: ${c.years_in_market}`,
          c.annual_revenue  && `• Ventas anuales aprox.: ${c.annual_revenue}`,
          c.main_products   && `• Portafolio / productos principales: ${c.main_products}`,
        ].filter(Boolean)

        const contextBlock = ctxLines.length > 0
          ? `CONTEXTO DEL CLIENTE YA REGISTRADO (no vuelvas a pedir esta información):\n${ctxLines.join('\n')}`
          : `No hay contexto adicional del cliente registrado aún.`

        const initMsg = `Proyecto activo: "${c.name}".

${contextBlock}

INSTRUCCIONES PARA ESTE INICIO:
1. Saluda brevemente y presenta lo que ya sabes del cliente usando el contexto de arriba.
2. Resume el estado actual del plan: qué fases están completas y cuáles faltan.
3. Pregunta ÚNICAMENTE por cuál fase o etapa quiere comenzar el consultor hoy.
IMPORTANTE: NO pidas información que ya está en el contexto (empresa, industria, país, tamaño, ventas, portafolio). Ve directo al trabajo.`

        const { reply } = await callAgentWithData(initMsg, pd)
        setLoading(false)
        addAgentMessage(reply)
      }
    } catch (e) {
      setLoadingProject(false)
      console.error('Error starting with project:', e)
    }
  }

  async function startWithoutProject() {
    setSelectedProject({ name: 'Nueva sesión', client_company: 'Sin proyecto' })
    setProjectData(null)
    setLoadingProject(false)
    startAgent()
  }

  async function startAgent() {
    setLoading(true)
    setLoadingStatus('Iniciando agente...')
    const { reply } = await callAgent('Inicia el proceso. Preséntate brevemente y pide el nombre de la empresa y la industria.')
    setLoading(false)
    addAgentMessage(reply)
  }

  async function callAgentWithData(userMsg, pd) {
    historyRef.current.push({ role: 'user', content: userMsg })
    setLoadingStatus('Pensando...')

    // Context compression: send only the last 20 messages to the API
    const MAX_HISTORY = 20
    const recentHistory = historyRef.current.slice(-MAX_HISTORY)
    const isCompressed = historyRef.current.length > MAX_HISTORY
    const compressionNote = isCompressed
      ? `[Nota: Esta conversación tiene ${historyRef.current.length} mensajes. Se muestran los últimos ${MAX_HISTORY} para optimizar el contexto.]\n\n`
      : ''

    const systemPrompt = buildSystemPrompt(pd)
    const conversationContext = [
      '[IDENTIDAD Y ROL DEL AGENTE]',
      systemPrompt,
      '',
      '[HISTORIAL DE CONVERSACIÓN]',
      compressionNote,
      ...recentHistory.map(m =>
        `${m.role === 'user' ? 'CONSULTOR' : 'AGENTE NVC'}: ${m.content}`
      ),
      '',
      '[INSTRUCCIÓN ACTUAL]',
      'Continúa la conversación como el AGENTE NVC. Responde únicamente al último mensaje del CONSULTOR. Sigue la metodología NVC, haz UNA sola pregunta a la vez y avanza por las fases/etapas del proceso.',
    ].join('\n\n')

    try {
      const { data, error } = await supabase.functions.invoke('ai-report', {
        body: {
          mode: 'chat',
          messages: recentHistory,
          systemPrompt: systemPrompt,
          context: conversationContext,
          reportType: 'agent_turn',
        }
      })

      if (error) {
        // Surface the actual error body from the edge function
        let detail = error.message
        try {
          if (error.context) {
            const body = await error.context.json()
            if (body?.error) detail = body.error
          }
        } catch {}
        throw new Error(detail)
      }

      if (data?.searches?.length) {
        setLoadingStatus(`Buscando en web... (${data.searches.length} búsquedas)`)
        if (data.searchEnabled) setSearchEnabled(true)
        const searchNote = data.searches.map(s => `🔍 **Búsqueda:** ${s.query}`).join('\n')
        setMessages(prev => [...prev, { role: 'system', text: searchNote }])
      }

      const reply = data?.report || ''
      if (!reply) throw new Error('Respuesta vacía del servidor')
      historyRef.current.push({ role: 'assistant', content: reply })
      return { reply, searches: data?.searches || [] }
    } catch (e) {
      console.error('callAgentWithData error:', e.message)
      const fallback = `⚠️ Error al conectar con el motor de IA: ${e.message}`
      historyRef.current.push({ role: 'assistant', content: fallback })
      return { reply: fallback, searches: [] }
    }
  }

  async function callAgent(userMsg) {
    historyRef.current.push({ role: 'user', content: userMsg })
    setLoadingStatus('Pensando...')

    const MAX_HISTORY = 20
    const recentHistory = historyRef.current.slice(-MAX_HISTORY)
    const isCompressed = historyRef.current.length > MAX_HISTORY

    const conversationContext = [
      '[IDENTIDAD Y ROL DEL AGENTE]',
      SYSTEM_PROMPT,
      '',
      '[HISTORIAL DE CONVERSACIÓN]',
      isCompressed ? `[Últimos ${MAX_HISTORY} de ${historyRef.current.length} mensajes]\n` : '',
      ...recentHistory.map(m =>
        `${m.role === 'user' ? 'CONSULTOR' : 'AGENTE NVC'}: ${m.content}`
      ),
      '',
      '[INSTRUCCIÓN ACTUAL]',
      'Continúa la conversación como el AGENTE NVC. Responde únicamente al último mensaje del CONSULTOR. Sigue la metodología NVC, haz UNA sola pregunta a la vez y avanza por las fases/etapas del proceso.',
    ].join('\n\n')

    try {
      const { data, error } = await supabase.functions.invoke('ai-report', {
        body: {
          mode: 'chat',
          messages: recentHistory,
          systemPrompt: SYSTEM_PROMPT,
          context: conversationContext,
          reportType: 'agent_turn',
        }
      })
      if (error) throw error

      if (data?.searches?.length) {
        setLoadingStatus(`Buscando en web... (${data.searches.length} búsquedas)`)
        if (data.searchEnabled) setSearchEnabled(true)
        const searchNote = data.searches.map(s => `🔍 **Búsqueda:** ${s.query}`).join('\n')
        setMessages(prev => [...prev, { role: 'system', text: searchNote }])
      }

      const reply = data?.report || ''
      if (!reply) throw new Error('Respuesta vacía del servidor')
      historyRef.current.push({ role: 'assistant', content: reply })
      return { reply, searches: data?.searches || [] }
    } catch (e) {
      const fallback = `⚠️ Error al conectar con el motor de IA: ${e.message}`
      historyRef.current.push({ role: 'assistant', content: fallback })
      return { reply: fallback, searches: [] }
    }
  }

  // Normalize a string: lowercase, strip accents/diacritics, collapse whitespace
  function normStr(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }

  // Detect which sectionKey a phase name belongs to (f1, f2, f3, e1…)
  function detectSectionKey(name) {
    const n = normStr(name)
    if (/f1|pestel|macro|analisis macro/.test(n)) return 'f1'
    if (/f2|industria/.test(n)) return 'f2'
    if (/f3|mercados objetivo|priorizacion/.test(n)) return 'f3'
    if (/unidad.*negocio|negocio.*unidad|\bbu\b/.test(n)) return 'bu'
    if (/\be1\b|analisis del mercado|tamano|cagr/.test(n)) return 'e1'
    if (/\be2\b|consumidor/.test(n)) return 'e2'
    if (/\be3\b|competitiv/.test(n)) return 'e3'
    if (/\be4\b|interno|portafolio/.test(n)) return 'e4'
    if (/\be5\b|estrategia por mercado/.test(n)) return 'e5'
    if (/f6|estrategia corporativa/.test(n)) return 'f6'
    return null
  }

  async function executeSave(save) {
    if (!projectData || !save) return

    setLoading(true)
    try {
      // Use the full agent response content, not just the last message
      const content = save.content || messages.filter(m => m.role === 'assistant').slice(-1)[0]?.text || ''

      // Load all phases for this consultancy
      const { data: allPhases } = await supabase
        .from('phases')
        .select('id, name')
        .eq('consultancy_id', projectData.consultancy.id)

      // 1) Exact match (normalized)
      const normSave = normStr(save.phaseName)
      let matchedPhase = (allPhases || []).find(p => normStr(p.name) === normSave)

      // 2) sectionKey match: detect the key from the save name, then find a phase with the same key
      if (!matchedPhase) {
        const key = detectSectionKey(save.phaseName)
        if (key) {
          matchedPhase = (allPhases || []).find(p => detectSectionKey(p.name) === key)
        }
      }

      // 3) Word-overlap fallback (normalized, no accent issues)
      if (!matchedPhase) {
        const searchWords = normSave.split(/\s+/).filter(w => w.length > 3)
        matchedPhase = (allPhases || []).find(p => {
          const pn = normStr(p.name)
          return searchWords.length > 0 &&
            searchWords.filter(w => pn.includes(w)).length >= Math.ceil(searchWords.length * 0.35)
        })
      }

      if (matchedPhase) {
        await saveReportToPhase(projectData.consultancy.id, matchedPhase.id, content, 'agent_generated', profile?.id)
        setMessages(prev => [...prev, { role: 'system', text: `✅ Informe guardado en **${matchedPhase.name}** correctamente.` }])
      } else {
        // No matching phase — save as a consultancy-level report anyway
        await supabase.from('ai_reports').insert({
          consultancy_id: projectData.consultancy.id,
          entity_type: 'consultancy',
          entity_id: projectData.consultancy.id,
          report_type: 'agent_generated',
          content: `[${save.phaseName}]\n\n${content}`,
          generated_by: profile?.id,
        })
        setMessages(prev => [...prev, { role: 'system', text: `✅ Informe guardado como reporte general (fase "${save.phaseName}" no existe aún en la app).` }])
      }
      // Note: queue advancement (markCurrentAnswered) is handled by finalizeAgentMessage
      // via ✅ detection, so we do NOT call it here to avoid advancing the queue twice.
    } catch (e) {
      setMessages(prev => [...prev, { role: 'system', text: `❌ Error al guardar: ${e.message}` }])
    }
    setPendingSave(null)
    setLoading(false)
  }

  function finalizeAgentMessage(text, skipSave = false) {
    const { cleanText, commands } = parseAgentCommands(text)
    setMessages(prev => {
      const updated = [...prev, { role: 'assistant', text: cleanText }]
      if (!skipSave && projectData?.consultancy?.id) {
        scheduleSave(projectData.consultancy.id, updated, historyRef.current)
      }
      return updated
    })

    for (const cmd of commands) {
      if (cmd.type === 'save_report') {
        setPendingSave({ phaseName: cmd.phaseName, content: cleanText })
      }
    }

    // Auto-complete tasks in Supabase AND advance the agent queue
    if (text.includes('✅')) {
      autoMarkSectionsComplete(text)
      // Advance the autonomous queue whenever the agent marks a section complete,
      // regardless of whether we're in autonomous mode or normal chat mode.
      // This keeps the status panel in sync with the actual conversation progress.
      markCurrentAnswered()
    }

    const detected = detectPhase(text)
    if (detected) {
      const detectedIdx = PHASE_ORDER.indexOf(detected)
      const currentIdx = PHASE_ORDER.indexOf(currentPhase)
      if (detectedIdx > currentIdx) {
        setCompletedPhases(prev => [...prev, currentPhase])
        setCurrentPhase(detected)
      }
    }
    const lo = text.toLowerCase()
    if (lo.includes('¿correcto') || lo.includes('confirma')) setQuickReplies(['Sí, correcto', 'Hay algo que ajustar'])
    else if (lo.includes('avanzamos') || lo.includes('siguiente')) setQuickReplies(['Sí, avancemos', 'Quiero agregar más información'])
    else if (lo.includes('alta') || lo.includes('media') || lo.includes('baja')) setQuickReplies(['Alta', 'Media', 'Baja'])
    else if (lo.includes('prioridad 1') || lo.includes('prioridad 2')) setQuickReplies(['Prioridad 1', 'Prioridad 2', 'Prioridad 3', 'Nuevo ingreso'])
    else setQuickReplies([])
  }

  function addAgentMessage(text, skipSave = false) {
    // Stream the response word by word, then finalize
    streamText(text, () => finalizeAgentMessage(text, skipSave))
  }

  async function send(text) {
    const msg = (text || input).trim()
    if (!msg || loading) return
    setInput('')
    setQuickReplies([])
    setMessages(prev => [...prev, { role: 'user', text: msg }])
    setLoading(true)
    if (exchanges <= 2 && msg.length > 2) {
      const parts = msg.split(/[\n,\/·\-]/)
      if (!empresa && parts[0]?.trim().length > 1) setEmpresa(parts[0].trim())
      if (!industria && parts[1]?.trim().length > 1) setIndustria(parts[1].trim())
    }

    if (isAutonomousMode && projectData) {
      // In autonomous mode, track when the section gets completed
      await handleAutonomousAnswer(msg, projectData)
      return
    }

    let reply
    if (projectData) {
      const res = await callAgentWithData(msg, projectData)
      reply = res.reply
    } else {
      const res = await callAgent(msg)
      reply = res.reply
    }

    setLoading(false)
    setExchanges(e => e + 1)
    addAgentMessage(reply)
  }

  async function tavilySearch(query) {
    const res = await fetch('https://api.tavily.com/search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        api_key: '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',
        query,
        search_depth: 'basic',
        max_results: 6,
        include_answer: true,
        include_raw_content: false,
      }),
    })
    if (!res.ok) throw new Error(`Tavily ${res.status}`)
    const data = await res.json()
    let output = ''
    if (data.answer) output += `RESPUESTA DIRECTA: ${data.answer}\n\n`
    if (data.results?.length) {
      output += 'FUENTES:\n'
      for (const r of data.results) {
        output += `• ${r.title}\n  ${(r.content || '').slice(0, 350)}\n  Fuente: ${r.url}\n\n`
      }
    }
    return output || 'Sin resultados relevantes.'
  }

  async function investigateStage() {
    const phaseName = PHASE_NAMES[currentPhase] || currentPhase
    const empresa_ = empresa || 'la empresa cliente'
    const industria_ = industria || 'la industria'

    setLoading(true)
    setLoadingStatus('Buscando en web...')
    setMessages(prev => [...prev, { role: 'user', text: `🔍 Investigar en web: "${phaseName}"` }])

    try {
      const queries = buildSearchQueries(phaseName, empresa_, industria_)
      const searchResults = []

      for (const q of queries) {
        setLoadingStatus(`Buscando: ${q}`)
        const result = await tavilySearch(q)
        searchResults.push({ query: q, result })
        setMessages(prev => [...prev, { role: 'system', text: `🔍 **Búsqueda:** ${q}` }])
      }

      setSearchEnabled(true)

      const searchContext = searchResults
        .map(s => `BÚSQUEDA: "${s.query}"\n${s.result}`)
        .join('\n---\n')

      const synthesisMsg = `He buscado información en internet sobre "${phaseName}" para ${empresa_} en ${industria_}. Con base en los siguientes resultados reales, completa y estructura esta etapa con datos concretos:\n\n${searchContext}\n\nPresenta la información de forma estructurada siguiendo el formato de la metodología NVC para esta etapa.`

      let reply
      if (projectData) {
        const res = await callAgentWithData(synthesisMsg, projectData)
        reply = res.reply
      } else {
        const res = await callAgent(synthesisMsg)
        reply = res.reply
      }
      setLoading(false)
      setExchanges(e => e + 1)
      addAgentMessage(reply)
    } catch (e) {
      setLoading(false)
      addAgentMessage(`⚠️ Error en búsqueda web: ${e.message}`)
    }
  }

  function buildSearchQueries(phaseName, empresa, industria) {
    const year = new Date().getFullYear()
    const base = `${empresa} ${industria}`
    const phaseId = currentPhase

    if (phaseId.startsWith('f1')) return [`análisis PESTEL ${industria} ${year}`, `tendencias macroeconómicas ${industria} ${year}`]
    if (phaseId === 'f2-1') return [`segmentación mercado ${industria} ${year}`]
    if (phaseId === 'f2-2') return [`análisis competitivo ${industria} cinco fuerzas Porter ${year}`]
    if (phaseId === 'f2-3') return [`tendencias industria ${industria} ${year}`, `innovaciones ${industria}`]
    if (phaseId.startsWith('f3')) return [`mercados potenciales ${base} ${year}`, `oportunidades expansión ${industria}`]
    if (phaseId === 'e1-1' || phaseId === 'e1-2') return [`tamaño mercado ${base} ${year}`, `crecimiento ${industria} CAGR ${year}`]
    if (phaseId === 'e1-3') return [`estacionalidad demanda ${industria} ${year}`]
    if (phaseId.startsWith('e2')) return [`comportamiento consumidor ${industria} ${year}`, `tendencias consumo ${base}`]
    if (phaseId.startsWith('e3')) return [`principales competidores ${base} ${year}`, `market share ${industria}`]
    if (phaseId.startsWith('e4')) return [`mejores prácticas ${industria} estrategia ${year}`]
    if (phaseId.startsWith('e5')) return [`estrategia de negocio ${industria} ${year}`, `posicionamiento marcas ${industria}`]
    if (phaseId.startsWith('f6')) return [`estrategia corporativa ${industria} ${year}`]
    return [`${phaseName} ${base} ${year}`, `${industria} análisis estratégico ${year}`]
  }

  async function generateReport() {
    setLoading(true)
    setLoadingStatus('Generando informe estratégico...')
    try {
      const { data, error } = await supabase.functions.invoke('ai-report', {
        body: {
          messages: historyRef.current,
          reportType: 'strategic_plan',
          entityType: 'consultancy',
          mode: 'report'
        }
      })
      if (error) throw error
      addAgentMessage(`✅ **Informe estratégico generado**\n\n${data?.report || data?.content || ''}`)
      if (profile?.id) {
        await supabase.from('ai_reports').insert({
          consultancy_id: projectData?.consultancy?.id || null,
          entity_type: 'agent_session',
          entity_id: null,
          report_type: 'strategic_plan',
          content: data?.report || '',
          generated_by: profile.id,
        })
      }
    } catch (e) {
      addAgentMessage(`❌ Error generando informe: ${e.message}`)
    }
    setLoading(false)
  }

  function exportConversation() {
    const content = historyRef.current
      .map(m => `[${m.role === 'user' ? 'CONSULTOR' : 'AGENTE NVC'}]\n${m.content}`)
      .join('\n\n' + '─'.repeat(50) + '\n\n')
    const blob = new Blob([
      `PROCESO ESTRATÉGICO NVC\nEmpresa: ${empresa}\nFecha: ${new Date().toLocaleDateString('es-CO')}\n\n${'═'.repeat(50)}\n\n${content}`
    ], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `nvc-${(empresa || 'consulta').toLowerCase().replace(/\s/g, '-')}.txt`
    a.click()
  }

  function resetSession() {
    // "Nueva sesión" en el agente solo limpia la vista local, no borra el historial guardado
    historyRef.current = []
    setMessages([])
    setCurrentPhase('init')
    setCompletedPhases([])
    setEmpresa('')
    setIndustria('')
    setExchanges(0)
    setQuickReplies([])
    setPendingSave(null)
    setHistoryRestored(false)
    startAgent()
  }

  function changeProject() {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    convRecordIdRef.current = null
    setSelectedProject(null)
    setProjectData(null)
    setMessages([])
    historyRef.current = []
    setCurrentPhase('init')
    setCompletedPhases([])
    setEmpresa('')
    setIndustria('')
    setExchanges(0)
    setQuickReplies([])
    setPendingSave(null)
    setUploadedDocs([])
    setHistoryRestored(false)
  }

  async function clearConversationHistory() {
    if (!projectData?.consultancy?.id) return
    if (!window.confirm('¿Seguro que quieres iniciar una nueva sesión? La conversación anterior quedará guardada en el historial.')) return
    // Don't delete — just start a new session (convRecordIdRef = null → next save creates new record)
    convRecordIdRef.current = null
    historyRef.current = []
    setMessages([])
    setHistoryRestored(false)
    setCurrentPhase('init')
    setCompletedPhases([])
    setExchanges(0)
    setShowSessions(false)
    setLoading(true)
    const initMsg = `Nueva sesión para el proyecto: ${projectData.consultancy.name}. Revisa el estado actual del proyecto y pregunta por cuál etapa quiere trabajar hoy.`
    const { reply } = await callAgentWithData(initMsg, projectData)
    setLoading(false)
    addAgentMessage(reply)
    loadAllSessions(projectData.consultancy.id)
  }

  // ── File text extraction ──────────────────────────────────────────────────
  async function extractTextFromFile(file) {
    const name = file.name.toLowerCase()
    try {
      if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.csv')) {
        return await file.text()
      }
      if (name.endsWith('.docx')) {
        const mammoth = (await import('mammoth')).default
        const buffer = await file.arrayBuffer()
        const result = await mammoth.extractRawText({ arrayBuffer: buffer })
        return result.value
      }
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const XLSX = await import('xlsx')
        const buffer = await file.arrayBuffer()
        const wb = XLSX.read(buffer, { type: 'array' })
        return wb.SheetNames.map(sn => {
          const ws = wb.Sheets[sn]
          return `[Hoja: ${sn}]\n${XLSX.utils.sheet_to_csv(ws)}`
        }).join('\n\n')
      }
      if (name.endsWith('.pptx') || name.endsWith('.ppt')) {
        // Extract text from each slide using JSZip (pptx is a zip of XML files)
        const JSZip = (await import('jszip')).default
        const buffer = await file.arrayBuffer()
        const zip = await JSZip.loadAsync(buffer)
        const slideFiles = Object.keys(zip.files)
          .filter(f => f.match(/ppt\/slides\/slide\d+\.xml$/))
          .sort((a, b) => {
            const na = parseInt(a.match(/slide(\d+)/)?.[1] || 0)
            const nb = parseInt(b.match(/slide(\d+)/)?.[1] || 0)
            return na - nb
          })
        const slides = await Promise.all(slideFiles.map(async (sf, i) => {
          const xml = await zip.files[sf].async('string')
          // Extract text nodes from XML
          const text = xml
            .replace(/<a:t>/g, ' ')
            .replace(/<\/a:t>/g, '')
            .replace(/<[^>]+>/g, '')
            .replace(/\s+/g, ' ')
            .trim()
          return `[Diapositiva ${i + 1}]: ${text}`
        }))
        return slides.filter(s => s.length > 20).join('\n\n')
      }
      if (name.endsWith('.pdf')) {
        // Try reading as text (works for text-based PDFs)
        const text = await file.text()
        const readable = text.replace(/[^\x20-\x7E\n\t]/g, ' ').replace(/\s+/g, ' ').trim()
        if (readable.length > 100) return readable.slice(0, 8000)
        return `[PDF adjunto: ${file.name}. El contenido no pudo ser extraído automáticamente.]`
      }
      if (file.type.startsWith('image/')) {
        return `[Imagen adjunta: ${file.name}]`
      }
      return `[Archivo adjunto: ${file.name}]`
    } catch (e) {
      return `[Error al leer ${file.name}: ${e.message}]`
    }
  }

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setShowDropzone(false)
    // Also process rejected files that have accepted extensions (MIME type mismatch on some browsers)
    const allowedExts = ['.pptx', '.ppt', '.docx', '.xlsx', '.xls', '.pdf', '.txt', '.md', '.csv', '.png', '.jpg', '.jpeg', '.gif', '.webp']
    const recoveredFiles = (rejectedFiles || [])
      .map(r => r.file)
      .filter(f => allowedExts.some(ext => f.name.toLowerCase().endsWith(ext)))
    const allFiles = [...acceptedFiles, ...recoveredFiles]
    for (const file of allFiles) {
      setLoadingStatus(`Leyendo ${file.name}...`)
      let text = ''
      try {
        text = await extractTextFromFile(file)
      } catch (e) {
        setMessages(prev => [...prev, { role: 'system', text: `❌ No se pudo leer el archivo: ${e.message}` }])
        continue
      }

      const doc = { name: file.name, size: file.size, text }
      setUploadedDocs(prev => [...prev, doc])

      // Truncate at 5000 chars to keep the prompt within edge function limits
      const truncated = text.length > 5000
      const docMsg = `[DOCUMENTO ADJUNTO: ${file.name}]\n${text.slice(0, 5000)}${truncated ? '\n...(contenido truncado por longitud)' : ''}`

      setMessages(prev => [...prev, {
        role: 'system',
        text: `📎 Documento adjunto: **${file.name}** (${(file.size / 1024).toFixed(0)} KB)${truncated ? ' — texto truncado a 5000 caracteres' : ''}`
      }])

      // Send to agent
      setLoading(true)
      setLoadingStatus('Analizando documento...')
      try {
        let reply
        if (projectData) {
          const res = await callAgentWithData(docMsg, projectData)
          reply = res.reply
        } else {
          const res = await callAgent(docMsg)
          reply = res.reply
        }
        setLoading(false)
        addAgentMessage(reply)
      } catch (e) {
        setLoading(false)
        setMessages(prev => [...prev, {
          role: 'system',
          text: `❌ Error procesando el documento: ${e.message}. Intenta con un archivo más pequeño o en formato .txt`
        }])
      }
    }
  }, [projectData])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // Use validator instead of accept so MIME-type mismatches don't block files
    validator: (file) => {
      const allowed = ['.pptx','.ppt','.docx','.xlsx','.xls','.pdf','.txt','.md','.csv','.png','.jpg','.jpeg','.gif','.webp']
      const ok = allowed.some(ext => file.name.toLowerCase().endsWith(ext))
      return ok ? null : { code: 'file-invalid-type', message: 'Tipo de archivo no soportado' }
    },
    noClick: true,
    noKeyboard: true,
  })

  // ── Word export ───────────────────────────────────────────────────────────
  async function exportAsWord() {
    const companyName = selectedProject?.client_company || empresa || 'Empresa Cliente'
    const projectName = selectedProject?.name || empresa || 'Plan Estratégico'
    const date = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })

    const children = [
      new Paragraph({
        text: 'PLAN ESTRATÉGICO',
        heading: HeadingLevel.TITLE,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
      new Paragraph({
        children: [new TextRun({ text: companyName, bold: true, size: 32, color: '1a3f6b' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [new TextRun({ text: `Next Valley Consultants · Metodología NVC v2.0`, size: 22, color: '6b7280', italics: true })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [new TextRun({ text: `Fecha: ${date}`, size: 20, color: '6b7280' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 600 },
      }),
      new Paragraph({
        text: 'PROCESO DE CONSULTORÍA ESTRATÉGICA',
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 },
      }),
    ]

    for (const msg of historyRef.current) {
      if (msg.role === 'user') {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: '▶ CONSULTOR:', bold: true, color: '1a3f6b', size: 22 })],
            spacing: { before: 300, after: 80 },
          }),
          ...msg.content.split('\n').filter(Boolean).map(line =>
            new Paragraph({
              children: [new TextRun({ text: line, size: 20, color: '374151' })],
              spacing: { after: 60 },
              indent: { left: 400 },
            })
          )
        )
      } else {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: '◆ AGENTE NVC:', bold: true, color: '065f46', size: 22 })],
            spacing: { before: 240, after: 80 },
          }),
          ...msg.content.split('\n').filter(Boolean).map(line => {
            const isBold = line.startsWith('**') && line.endsWith('**')
            const isHeading = line.startsWith('#')
            const cleanLine = line.replace(/\*\*/g, '').replace(/^#+\s*/, '')
            return new Paragraph({
              children: [new TextRun({
                text: cleanLine,
                bold: isBold || isHeading,
                size: isHeading ? 24 : 20,
                color: isHeading ? '065f46' : '1f2937',
              })],
              spacing: { after: 60 },
              indent: { left: 400 },
            })
          })
        )
      }
    }

    children.push(
      new Paragraph({
        children: [new TextRun({ text: `Documento generado el ${date} mediante el Agente Estratégico NVC`, size: 16, color: '9ca3af', italics: true })],
        alignment: AlignmentType.CENTER,
        spacing: { before: 600 },
      })
    )

    const doc = new Document({
      sections: [{ properties: {}, children }],
      styles: {
        default: { document: { run: { font: 'Calibri', size: 22 } } }
      }
    })

    const blob = await Packer.toBlob(doc)
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `plan-estrategico-${(companyName).toLowerCase().replace(/\s+/g, '-')}.docx`
    a.click()
  }

  if (selectedProject === null) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="w-64 bg-brand-900 text-white flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-4 border-b border-brand-700">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center text-xs font-bold">NV</div>
              <div>
                <div className="text-sm font-bold">Agente Estratégico</div>
                <div className="text-xs text-brand-300">NVC v2.0</div>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-4 px-3 space-y-2 text-sm text-brand-200">
            <p className="text-xs text-brand-400">Selecciona un proyecto o crea una nueva sesión.</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
            <Bot size={20} className="text-brand-600" />
            <div>
              <div className="font-semibold text-gray-900">Agente Estratégico NVC</div>
              <div className="text-xs text-gray-500">Selecciona un proyecto para comenzar</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Proyectos Disponibles</h2>
                <p className="text-gray-600 text-sm">Selecciona una consultoría existente o comienza una nueva sesión sin proyecto</p>
              </div>

              {loadingProject && (
                <div className="flex items-center justify-center py-12">
                  <div className="flex gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'0ms'}}/>
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'150ms'}}/>
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'300ms'}}/>
                  </div>
                </div>
              )}

              {!loadingProject && consultancies.length > 0 && (
                <div className="grid gap-4 mb-8">
                  {consultancies.map(c => (
                    <div
                      key={c.id}
                      onClick={() => startWithProject(c)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{c.name}</div>
                          <div className="text-sm text-gray-600 mt-1">{c.client_company || 'Sin empresa cliente'}</div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              c.status === 'active' ? 'bg-green-100 text-green-700' :
                              c.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {c.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-brand-600">→</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={startWithoutProject}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-all text-left"
              >
                <div className="font-semibold text-gray-900">+ Nueva sesión sin proyecto</div>
                <div className="text-sm text-gray-600 mt-1">Comienza un análisis estratégico sin vincular a una consultoría existente</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const mainPhases = ['f1','f2','f3','e1','e2','e3','e4','e5','f6']
  const currentMainIdx = mainPhases.findIndex(p =>
    currentPhase === p || currentPhase.startsWith(p + '-')
  )

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="w-64 bg-brand-900 text-white flex flex-col overflow-hidden flex-shrink-0">
        <div className="p-4 border-b border-brand-700">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-lg flex items-center justify-center text-xs font-bold">NV</div>
            <div>
              <div className="text-sm font-bold">Agente Estratégico</div>
              <div className="text-xs text-brand-300">NVC v2.0</div>
            </div>
            <div className="ml-auto w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
          </div>
          <div className="mt-3">
            <div className="flex gap-0.5">
              {mainPhases.map((_, i) => (
                <div key={i} className={`flex-1 h-1 rounded-full transition-all duration-500 ${
                  i < currentMainIdx ? 'bg-green-400' : i === currentMainIdx ? 'bg-blue-400' : 'bg-brand-700'
                }`}/>
              ))}
            </div>
            <div className="text-xs text-brand-400 mt-1">{PHASE_NAMES[currentPhase] || 'Inicio'}</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {SIDEBAR_ITEMS.map(item => {
            const idx = PHASE_ORDER.indexOf(item.id)
            const currentIdx = PHASE_ORDER.indexOf(currentPhase)
            const isDone = idx < currentIdx
            const isActive = item.id === currentPhase

            // Find the matching DB phase to link to (only for top-level items)
            const phases = projectData?.phases || []
            const parentKey = item.id.includes('-') ? item.id.split('-')[0] : item.id
            const matchedPhase = item.level === 0
              ? phases.find(p => detectSectionKey(p.name) === item.id)
              : phases.find(p => detectSectionKey(p.name) === parentKey)
            const consultancyId = projectData?.consultancy?.id || activeConsultancyId

            return (
              <div key={item.id}>
                {item.divider && (
                  <div className="px-4 pt-4 pb-1 text-xs font-bold uppercase tracking-widest text-brand-500">
                    {item.divider}
                  </div>
                )}
                <div
                  onClick={matchedPhase && consultancyId
                    ? () => navigate(`/consultancy/${consultancyId}/phase/${matchedPhase.id}`)
                    : undefined
                  }
                  className={`flex items-center gap-2 py-1.5 text-xs transition-all
                    ${item.level === 1 ? 'pl-8 pr-4' : 'px-4'}
                    ${isActive ? 'bg-blue-600/20 text-blue-300 border-l-2 border-blue-400' : isDone ? 'text-green-400' : 'text-brand-400'}
                    ${matchedPhase ? 'cursor-pointer hover:bg-brand-700/50 hover:text-white' : ''}
                  `}>
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0
                    ${isActive ? 'bg-blue-400' : isDone ? 'bg-green-400' : 'bg-brand-600'}
                  `}/>
                  {item.label}
                  {isDone && <span className="ml-auto text-green-400">✓</span>}
                  {matchedPhase && !isDone && <span className="ml-auto text-brand-600 opacity-60">→</span>}
                </div>
              </div>
            )
          })}
        </div>

        {/* Sessions panel */}
        {sessions.length > 1 && (
          <div className="border-t border-brand-700">
            <button
              onClick={() => setShowSessions(s => !s)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-brand-300 hover:text-white hover:bg-brand-800 transition-all"
            >
              <Clock size={12} />
              <span className="flex-1 text-left">Sesiones anteriores ({sessions.length})</span>
              {showSessions ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
            {showSessions && (
              <div className="max-h-40 overflow-y-auto">
                {sessions.map((s, i) => {
                  const stored = (() => { try { return JSON.parse(s.content) } catch { return {} } })()
                  const msgCount = (stored.messages || []).filter(m => m.role !== 'system').length
                  const dateStr = new Date(s.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })
                  const isActive = s.id === convRecordIdRef.current
                  return (
                    <button
                      key={s.id}
                      onClick={() => switchSession(s)}
                      className={`w-full text-left px-4 py-1.5 text-xs transition-all ${
                        isActive ? 'bg-blue-600/30 text-blue-300' : 'text-brand-400 hover:text-white hover:bg-brand-800'
                      }`}
                    >
                      <span className="font-medium">{i === 0 && !isActive ? '● ' : isActive ? '▶ ' : '○ '}</span>
                      {dateStr} · {msgCount} msgs
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <div className="p-3 border-t border-brand-700 space-y-1">
          {selectedProject && selectedProject !== 'none' && (
            <>
              <div className="text-xs">
                <span className="text-brand-400">Proyecto:</span>{' '}
                <span className="text-white truncate">{selectedProject.name}</span>
              </div>
              {selectedProject.client_company && (
                <div className="text-xs">
                  <span className="text-brand-400">Empresa:</span>{' '}
                  <span className="text-white">{selectedProject.client_company}</span>
                </div>
              )}
            </>
          )}
          {empresa && (
            <div className="text-xs">
              <span className="text-brand-400">Empresa:</span>{' '}
              <span className="text-white">{empresa}</span>
            </div>
          )}
          {industria && (
            <div className="text-xs">
              <span className="text-brand-400">Industria:</span>{' '}
              <span className="text-white">{industria}</span>
            </div>
          )}
          {historyRef.current.length > 20 && (
            <div className="text-xs text-brand-500 mt-1">
              ⚡ {historyRef.current.length} msgs · ventana: últimos 20
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Autonomous mode header */}
        {isAutonomousMode && autonomousQuestion && (
          <div className="border-b px-4 py-2 flex items-center gap-3 text-xs bg-brand-700 border-brand-600">
            <div className="flex items-center gap-2 text-white">
              <Zap size={12} className="text-white" />
              <span className="font-semibold">Pregunta {autonomousQuestion.priority} de {AUTONOMOUS_QUESTIONS.length}</span>
              <span className="text-white/60">·</span>
              <span className="text-white/80">{autonomousQuestion.section}</span>
            </div>
            <button
              onClick={() => navigate(activeConsultancyId ? `/consultancy/${activeConsultancyId}` : '/')}
              className="ml-auto flex items-center gap-1 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={11} />
              Volver al proyecto
            </button>
          </div>
        )}

        {selectedProject && selectedProject.name !== 'Nueva sesión' && (
          <div className="bg-brand-50 border-b border-brand-100 px-4 py-1.5 flex items-center gap-2 text-xs">
            <span className="font-medium text-brand-700">{selectedProject.name}</span>
            <span className="text-brand-400">·</span>
            <span className="text-brand-500">{selectedProject.client_company}</span>
            {historyRestored && !isAutonomousMode && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full ml-1">
                💬 Historial restaurado
              </span>
            )}
            {historyRestored && !isAutonomousMode && (
              <button
                onClick={clearConversationHistory}
                className="text-red-400 hover:text-red-600 text-xs"
                title="Borrar historial guardado y comenzar de nuevo"
              >
                ✕ Nueva conversación
              </button>
            )}
            {!isAutonomousMode && (
              <button
                onClick={changeProject}
                className="ml-auto text-brand-400 hover:text-brand-600 text-xs font-medium"
              >
                ← Cambiar proyecto
              </button>
            )}
          </div>
        )}

        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
          <Bot size={20} className="text-brand-600" />
          <div>
            <div className="font-semibold text-gray-900">Agente Estratégico NVC</div>
            <div className="text-xs text-gray-500">Metodología Next Valley Consultants v2.0</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {searchEnabled && (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 text-green-700 text-xs rounded-full">
                <Globe size={11} /> Búsqueda web activa
              </div>
            )}
            {exchanges >= 1 && currentPhase !== 'init' && (
              <button
                onClick={investigateStage}
                disabled={loading}
                title={`Buscar información en web para: ${PHASE_NAMES[currentPhase]}`}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium rounded-lg hover:bg-blue-100 transition-all disabled:opacity-50"
              >
                <Search size={13} /> Investigar etapa
              </button>
            )}
            {exchanges >= 4 && (
              <>
                <button
                  onClick={generateReport}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-brand-700 to-brand-500 text-white text-xs font-medium rounded-lg hover:from-brand-600 hover:to-brand-400 transition-all disabled:opacity-50"
                >
                  <Sparkles size={13} /> Generar Informe
                </button>
                <button
                  onClick={exportAsWord}
                  title="Exportar como Word profesional"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all"
                >
                  <FileText size={13} /> Word
                </button>
              </>
            )}
            <button
              onClick={resetSession}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all"
            >
              <RefreshCw size={13} /> Nueva sesión
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4" {...getRootProps()}>
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="fixed inset-0 z-50 bg-blue-500/20 border-4 border-dashed border-blue-400 flex items-center justify-center pointer-events-none">
              <div className="bg-white rounded-2xl px-8 py-6 shadow-xl text-center">
                <Paperclip size={32} className="text-blue-500 mx-auto mb-2" />
                <p className="text-lg font-bold text-blue-700">Suelta el archivo aquí</p>
                <p className="text-sm text-gray-500 mt-1">PDF, Word, Excel, imágenes, texto</p>
              </div>
            </div>
          )}
          {messages.map((msg, i) => {
            if (msg.role === 'system') {
              return (
                <div key={i} className="flex items-start gap-2 px-2">
                  <Globe size={13} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-500 italic">
                    {msg.text.split('\n').map((line, j) => (
                      <span key={j}>{line}{j < msg.text.split('\n').length - 1 ? ' · ' : ''}</span>
                    ))}
                  </div>
                </div>
              )
            }
            return (
              <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${msg.role === 'assistant'
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                    : 'bg-brand-100 text-brand-700'
                  }`}>
                  {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
                </div>
                <div className={`max-w-2xl rounded-2xl px-4 py-3 text-sm leading-relaxed
                  ${msg.role === 'assistant'
                    ? 'bg-white border border-gray-200 text-gray-800 prose prose-sm prose-headings:text-brand-800 prose-strong:text-gray-900 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded max-w-none'
                    : 'bg-brand-600 text-white'
                  }`}>
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({children}) => <h1 className="text-lg font-bold text-brand-800 mt-3 mb-1 first:mt-0">{children}</h1>,
                        h2: ({children}) => <h2 className="text-base font-bold text-brand-700 mt-3 mb-1 first:mt-0">{children}</h2>,
                        h3: ({children}) => <h3 className="text-sm font-bold text-gray-800 mt-2 mb-1 first:mt-0">{children}</h3>,
                        p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                        ul: ({children}) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
                        li: ({children}) => <li className="text-sm">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                        blockquote: ({children}) => <blockquote className="border-l-4 border-brand-300 pl-3 italic text-gray-600 my-2">{children}</blockquote>,
                        table: ({children}) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full">{children}</table></div>,
                        th: ({children}) => <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold">{children}</th>,
                        td: ({children}) => <td className="border border-gray-200 px-2 py-1">{children}</td>,
                        code: ({inline, children}) => inline
                          ? <code className="bg-gray-100 text-brand-700 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                          : <pre className="bg-gray-100 rounded-lg p-3 overflow-x-auto text-xs font-mono my-2"><code>{children}</code></pre>,
                        hr: () => <hr className="border-gray-200 my-3" />,
                      }}
                    >
                      {msg.text}
                    </ReactMarkdown>
                  ) : (
                    <p>{msg.text}</p>
                  )}
                </div>
              </div>
            )
          })}

          {/* Streaming message (shown while text is being revealed) */}
          {isStreaming && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Bot size={16} className="text-white" />
              </div>
              <div className="max-w-2xl rounded-2xl px-4 py-3 text-sm bg-white border border-gray-200 text-gray-800 prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({children}) => <h1 className="text-lg font-bold text-brand-800 mt-3 mb-1 first:mt-0">{children}</h1>,
                    h2: ({children}) => <h2 className="text-base font-bold text-brand-700 mt-3 mb-1 first:mt-0">{children}</h2>,
                    h3: ({children}) => <h3 className="text-sm font-bold text-gray-800 mt-2 mb-1 first:mt-0">{children}</h3>,
                    p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                    ul: ({children}) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
                    li: ({children}) => <li className="text-sm">{children}</li>,
                    strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                  }}
                >
                  {streamingText}
                </ReactMarkdown>
                <span className="inline-block w-1.5 h-4 bg-blue-400 ml-0.5 animate-pulse rounded-sm" />
              </div>
            </div>
          )}

          {loading && !isStreaming && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Bot size={16} className="text-white" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                <div className="flex gap-2 items-center">
                  {(loadingStatus.includes('Buscando') || loadingStatus.includes('búsqueda')) && (
                    <Globe size={14} className="text-blue-500 animate-pulse" />
                  )}
                  <div className="flex gap-1 items-center">
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'0ms'}}/>
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'150ms'}}/>
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'300ms'}}/>
                  </div>
                  <span className="text-xs text-gray-400">{loadingStatus}</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="bg-white border-t border-gray-200 p-4">
          {pendingSave && (
            <div className="bg-green-50 border border-green-200 px-4 py-2 flex items-center gap-3 mb-3">
              <div className="flex-1 text-sm text-green-800">
                <span className="font-medium">💾 Listo para guardar:</span> {pendingSave.phaseName}
              </div>
              <button
                onClick={() => executeSave(pendingSave)}
                className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 flex items-center gap-1"
              >
                <Check size={12} /> Guardar en la app
              </button>
              <button
                onClick={() => setPendingSave(null)}
                className="text-green-600 hover:text-green-800 text-xs"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {quickReplies.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-3">
              {quickReplies.map(qr => (
                <button
                  key={qr}
                  onClick={() => send(qr)}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200 border border-gray-200 rounded-full text-xs text-gray-600 transition-all"
                >
                  {qr}
                </button>
              ))}
            </div>
          )}
          {uploadedDocs.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {uploadedDocs.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700">
                  <Paperclip size={11} />
                  <span className="max-w-[120px] truncate">{d.name}</span>
                  <button onClick={() => setUploadedDocs(p => p.filter((_, j) => j !== i))} className="text-blue-400 hover:text-blue-700 ml-1">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 items-end">
            <label className="flex-shrink-0 w-9 h-9 flex items-center justify-center border border-gray-200 rounded-xl text-gray-400 hover:text-brand-600 hover:border-brand-300 cursor-pointer transition-all" title="Adjuntar archivo">
              <input
                type="file"
                multiple
                className="hidden"
                accept=".txt,.md,.csv,.pdf,.docx,.xlsx,.xls,.pptx,.ppt,.png,.jpg,.jpeg,.gif,.webp"
                onChange={e => e.target.files && onDrop(Array.from(e.target.files))}
              />
              <Paperclip size={15} />
            </label>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => {
                setInput(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder="Escribe tu respuesta... o arrastra un archivo aquí (Enter para enviar)"
              rows={1}
              className="flex-1 resize-none border border-gray-300 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent min-h-[42px] max-h-[120px]"
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="w-10 h-10 bg-gradient-to-r from-brand-700 to-brand-500 text-white rounded-xl flex items-center justify-center hover:from-brand-600 hover:to-brand-400 transition-all disabled:opacity-50 flex-shrink-0"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
